import { useEffect, useState } from "react";
import { useNavigate, useLocation } from '@usas/ui-web/wrappers/ReactRouterDom';

import { COACH_ORG_ROLE_FIELD_NAME } from "../utils/ClubConstants";

import useClubStaffData from "../../../state/clubStaff/UseClubStaffData";
import useClubBaseInfoData from "../../../state/clubBaseInfo/UseClubBaseInfoData";

import useNavRoutes from "@usas/ui-web/state/security/UseNavRoutes";
import useBaseUsasClubData from "@usas/ui-web/state/clubBase/UseBaseUsasClubData";
import useEnvironmentVariableData from "@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData";
import useOrgRoleStaffRoleHierarchyData from "@usas/ui-web/state/orgRoleStaffRoleHierarchy/UseOrgRoleStaffRoleHierarchyData";

import HierarchicalDataUtils from "@usas/ui-web/utils/HierarchicalDataUtils";

const INITIAL_STATE = {
  pageErrorMessage: '',
  validationErrorMessage: '',
  clubStaffObj: null
};

const useClubStaffDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navRoutes } = useNavRoutes();
  const { baseUsasClubState } = useBaseUsasClubData();
  const { orgRoleStaffRoleHierarchyState } = useOrgRoleStaffRoleHierarchyData();
  const { clubBaseInfoState, getClubBaseInfo } = useClubBaseInfoData();
  const { clubStaffState, clubStaffPutState, clubStaffPostState, getClubStaffMembers, putClubStaff, postClubStaff
  } = useClubStaffData();
  const { environmentVariableState, HEAD_COACH_ORG_ROLE_ID, CLUB_STAFF_ROLE_GROUP_ID,
    CLUB_STAFF_REGISTRATION_ROLE_GROUP_ID
  } = useEnvironmentVariableData();
  const [state, setState] = useState(INITIAL_STATE);

  const onPageError = (error) => {
    setState({
      ...state,
      pageErrorMessage: error.message
    });
  };

  const onCancelClicked = (e) => {
    try {
      e?.preventDefault();

      navigate(navRoutes.CLUB_STAFF?.route);
    } catch (error) {
      onPageError(error);
    }
  };

  const onCloseValidationPopupClicked = (e) => {
    try {
      e?.preventDefault();

      setState({
        ...state,
        validationErrorMessage: ''
      });
    } catch (error) {
      onPageError(error);
    }
  };

  const onSubmitClubStaffAddForm = async (formState) => {
    try {
      const existingStaff = clubStaffState.arrayData.find(x => x.personId === formState.personId);

      if (existingStaff) {
        setState({
          ...state,
          validationErrorMessage: `${existingStaff.firstName} ${existingStaff.lastName} is already a member of this club staff`
        });
      } else {
        const uniqueRoleError = hasInvalidUniqueOrgRoles(formState, formState.personId);

        if (uniqueRoleError) {
          setState({
            ...state,
            validationErrorMessage: uniqueRoleError
          });
        } else {
          const clubStaffObj = {
            personId: formState.personId,
            personOrgRole: createPersonOrgRoleArray(formState.personOrgRole, formState.coachTypeCode),
          }

          await postClubStaff(baseUsasClubState.objData.orgUnitId, CLUB_STAFF_ROLE_GROUP_ID, clubStaffObj);
          await getClubStaffMembers(baseUsasClubState.objData.orgUnitId, CLUB_STAFF_ROLE_GROUP_ID);
          await getClubBaseInfo(baseUsasClubState.objData.usasClubId);

          navigate(navRoutes.CLUB_STAFF?.route);
        }
      }
    } catch (error) {
      onPageError(error);
    }
  };

  const onSubmitClubStaffEditForm = async (formState) => {
    try {
      const uniqueRoleError = hasInvalidUniqueOrgRoles(formState, state.clubStaffObj.personId);

      if (uniqueRoleError) {
        setState({
          ...state,
          validationErrorMessage: uniqueRoleError
        });
      } else {
        const formPersonOrgRoles = [...formState.personOrgRole];
        formState.registrationRoles.forEach(role => {
          if (formPersonOrgRoles.findIndex(x => x.id === role.id) === -1) {
            formPersonOrgRoles.push(role);
          }
        });

        const clubStaffObj = {
          personId: state.clubStaffObj.personId,
          personOrgRole: createPersonOrgRoleArray(formPersonOrgRoles, formState.coachTypeCode, state.clubStaffObj.personOrgRole),
        };

        await putClubStaff(baseUsasClubState.objData.orgUnitId, CLUB_STAFF_ROLE_GROUP_ID, state.clubStaffObj.personId, clubStaffObj);
        await getClubStaffMembers(baseUsasClubState.objData.orgUnitId, CLUB_STAFF_ROLE_GROUP_ID);
        await getClubBaseInfo(baseUsasClubState.objData.usasClubId);

        navigate(navRoutes.CLUB_STAFF?.route);
      }
    } catch (error) {
      onPageError(error);
    }
  };

  useEffect(() => {
    try {
      const element = document.querySelector(`#context-nav`);

      if (element) {
        element.scrollIntoView();
      }
    } catch (error) {
      onPageError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      if (location.state?.clubStaff) {
        setState({ ...state, clubStaffObj: location.state.clubStaff });
      }
    } catch (error) {
      onPageError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function hasInvalidUniqueOrgRoles(formState, personId = undefined) {
    const uniqueOrgRoleIds = [HEAD_COACH_ORG_ROLE_ID];

    for (const uniqueRoleId of uniqueOrgRoleIds) {
      const matchingPersonOrgRole = formState.personOrgRole.find(x => x.id === uniqueRoleId);

      if (matchingPersonOrgRole) {
        for (const staff of clubStaffState.arrayData) {
          if (staff.personId !== personId && Array.isArray(staff.personOrgRole)) {
            const matchingStaffRole = staff.personOrgRole.find(x => x.orgRoleId === uniqueRoleId);

            if (matchingStaffRole) {
              return `Can only have one ${matchingStaffRole.orgRoleName}, current ${matchingStaffRole.orgRoleName} is ${staff.firstName} ${staff.lastName}`;
            }
          }
        }
      }
    }

    return undefined;
  }

  function createPersonOrgRoleArray(formPersonOrgRoles, coachTypeCode, existingPersonOrgRoles = []) {
    const newPersonOrgRole = [];

    for (const role of formPersonOrgRoles) {
      const targetRole = HierarchicalDataUtils.FindNodeById(role.id, orgRoleStaffRoleHierarchyState.arrayData);
      const existingRole = existingPersonOrgRoles.find(x => x.orgRoleId === role.id) || {};

      if (Array.isArray(targetRole?.orgRoleField)) {
        const coachOrgRoleField = targetRole.orgRoleField.find(x => x.fieldName === COACH_ORG_ROLE_FIELD_NAME);
        const existingOrgRoleFields = Array.isArray(existingRole?.personOrgRoleFields) ? existingRole.personOrgRoleFields.filter((x) => x.fieldName !== COACH_ORG_ROLE_FIELD_NAME) : [];

        if (coachOrgRoleField) {
          newPersonOrgRole.push({
            ...existingRole,
            orgRoleId: role.id,
            orgRoleName: role.name,
            personOrgRoleFields: [
              ...existingOrgRoleFields,
              {
                fieldName: COACH_ORG_ROLE_FIELD_NAME,
                orgRoleFieldId: coachOrgRoleField.orgRoleFieldId,
                fieldValue: coachTypeCode
              }
            ]
          });
        } else {
          newPersonOrgRole.push({ ...existingRole, orgRoleId: role.id, orgRoleName: role.name, personOrgRoleFields: [...existingOrgRoleFields] });
        }
      } else {
        newPersonOrgRole.push({ ...existingRole, orgRoleId: role.id, orgRoleName: role.name, personOrgRoleFields: [] });
      }
    }

    return newPersonOrgRole;
  }

  return {
    isLoading: environmentVariableState.isLoading || clubStaffState.isArrayLoading || clubBaseInfoState.isObjLoading,
    isSaving: clubStaffPostState.isObjLoading || clubStaffPutState.isObjLoading,
    clubStaffObj: state.clubStaffObj || null,
    staffRoleGroupId: CLUB_STAFF_ROLE_GROUP_ID,
    registrationRoleGroupId: CLUB_STAFF_REGISTRATION_ROLE_GROUP_ID,
    state,
    onPageError,
    onCancelClicked,
    onSubmitClubStaffAddForm,
    onSubmitClubStaffEditForm,
    onCloseValidationPopupClicked
  };
};

export default useClubStaffDetail;